import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Printer,
  X,
  FileText,
  FileSpreadsheet,
  Layers,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Building,
  Scale,
} from 'lucide-react';
import {
  ManagementSummaryData,
  CyclePerformanceData,
  RaiserPerformanceData,
  MonthlyFinancialActivityRecord,
  AnnualFinancialSummaryRecord,
  ReportFilters,
} from '@/services/reportService';
import { formatCurrency, formatDate, formatNumber } from '@/lib/utils';

export type ReportPrintMode =
  | 'current_summary'
  | 'current_performance'
  | 'current_monthly'
  | 'management_pack';

interface ReportPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportMode: ReportPrintMode;
  selectedYear: string;
  summary: ManagementSummaryData;
  filteredCycles: CyclePerformanceData[];
  raiserPerformance: RaiserPerformanceData[];
  monthlyRecords: MonthlyFinancialActivityRecord[];
  annualSummary: AnnualFinancialSummaryRecord[];
  filters?: ReportFilters;
  availableFarms?: Array<{ id: string; name: string }>;
}

export const ReportPrintModal: React.FC<ReportPrintModalProps> = ({
  isOpen,
  onClose,
  reportMode,
  selectedYear,
  summary,
  filteredCycles,
  raiserPerformance,
  monthlyRecords,
  annualSummary,
  filters,
  availableFarms,
}) => {
  const printContainerRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  const generationDate = formatDate(new Date().toISOString());
  const generationTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const yearTitle =
    selectedYear === 'all'
      ? 'All Years (Multi-Year Portfolio)'
      : `Reporting Year ${selectedYear}`;

  const getReportTitle = () => {
    switch (reportMode) {
      case 'current_summary':
        return `Executive Management Summary — ${yearTitle}`;
      case 'current_performance':
        return `Production Performance & Cohort Analysis — ${yearTitle}`;
      case 'current_monthly':
        return `Monthly Financial Activity & Cashflow — ${yearTitle}`;
      case 'management_pack':
        return `Annual Management Report Pack — ${yearTitle}`;
    }
  };

  const getReportSubtitle = () => {
    switch (reportMode) {
      case 'current_summary':
        return 'Executive profitability, cash movement, swine inventory, and attention triage items.';
      case 'current_performance':
        return 'Cycle cohort economics, feed conversion, revenue, margin %, and raiser rankings.';
      case 'current_monthly':
        return '12-month calendar ledger cashflow breakdown, cost mix, and multi-year comparison.';
      case 'management_pack':
        return 'Comprehensive multi-section executive management pack combining operational and financial reports.';
    }
  };

  // Selected Farm Name
  const selectedFarmName =
    filters?.farmId && filters.farmId !== 'all'
      ? availableFarms?.find((f) => f.id === filters.farmId)?.name || filters.farmId
      : 'All Farms';

  // Sub-filter summary string for Performance View
  const filterSummaryString = filters
    ? [
        `Batch: ${filters.batchCode === 'all' ? 'All Batches' : filters.batchCode}`,
        `Farm: ${selectedFarmName}`,
        `Status: ${filters.status && filters.status !== 'all' ? filters.status.toUpperCase() : 'All Statuses'}`,
        filters.search ? `Search: "${filters.search}"` : null,
      ]
        .filter(Boolean)
        .join(' • ')
    : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden max-h-[94vh] flex flex-col bg-slate-100 border border-slate-300">
        {/* Preview Control Toolbar (Hidden when printing) */}
        <DialogHeader className="p-4 bg-slate-900 text-white flex flex-row items-center justify-between border-b border-slate-800 shrink-0 print:hidden">
          <div className="flex items-center gap-2.5">
            <Printer className="w-5 h-5 text-emerald-400" />
            <div>
              <DialogTitle className="text-sm font-bold text-white flex items-center gap-2">
                <span>Print Preview & PDF Export</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-950 text-emerald-300 border border-emerald-800 font-mono font-medium">
                  {reportMode === 'management_pack'
                    ? 'Multi-Page Pack'
                    : 'Current View'}
                </span>
              </DialogTitle>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Optimized for Chrome & Edge print preview. Choose "Save as PDF" in print dialog.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold gap-1.5 shadow-sm"
            >
              <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Printable Document Body Container */}
        <div
          ref={printContainerRef}
          className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-200/60 print:p-0 print:m-0 print:bg-white print:overflow-visible"
        >
          {/* Printable Report Surface */}
          <div className="max-w-[210mm] mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-md border border-slate-300 print:border-none print:shadow-none print:p-0 print:max-w-none print:w-full space-y-6 text-slate-900 font-sans text-xs">
            {/* Global Report Header */}
            <div className="border-b-2 border-slate-900 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-emerald-800 text-white font-extrabold text-[11px] tracking-wider uppercase">
                      VSB FARM
                    </span>
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Management Information System
                    </span>
                  </div>
                  <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mt-1.5">
                    {getReportTitle()}
                  </h1>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {getReportSubtitle()}
                  </p>
                </div>

                <div className="text-left sm:text-right text-[11px] text-slate-500 font-mono space-y-0.5 shrink-0 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <p>
                    <strong>Generated:</strong> {generationDate} at {generationTime}
                  </p>
                  <p>
                    <strong>Reporting Scope:</strong> {yearTitle}
                  </p>
                  {filterSummaryString && (
                    <p className="text-[10px] text-slate-600 max-w-[260px] truncate">
                      <strong>Filters:</strong> {filterSummaryString}
                    </p>
                  )}
                  <p className="text-[10px] text-emerald-800 font-semibold">
                    Live Operational Data Source
                  </p>
                </div>
              </div>
            </div>

            {/* =========================================================================
                RENDER SECTIONS ACCORDING TO REPORT MODE
                ========================================================================= */}

            {/* -------------------------------------------------------------------------
                MODE 1 & PACK: EXECUTIVE MANAGEMENT SUMMARY
                ------------------------------------------------------------------------- */}
            {(reportMode === 'current_summary' ||
              reportMode === 'management_pack') && (
              <div className="space-y-6 print:space-y-5">
                {reportMode === 'management_pack' && (
                  <div className="pb-1 border-b border-slate-300">
                    <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-800 text-white flex items-center justify-center text-[10px]">
                        1
                      </span>
                      <span>Executive Management Summary</span>
                    </h2>
                  </div>
                )}

                {/* 1. Financial Snapshot (4 KPIs) */}
                <div className="space-y-2 break-inside-avoid">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                    <span className="w-4 h-4 rounded-full bg-slate-800 text-white flex items-center justify-center text-[9px]">
                      1.1
                    </span>
                    <span>Financial Snapshot (Booked vs Cycle Performance)</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Booked Pig Sales
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-slate-900 mt-1">
                        {formatCurrency(summary.totalBookedRevenue)}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        Calendar cashflow posting
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Booked Production Costs
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-slate-900 mt-1">
                        {formatCurrency(summary.totalBookedProductionCosts)}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        Feed, piglets, meds & exp
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/50">
                      <span className="text-[10px] font-bold uppercase text-emerald-800 block">
                        Completed-Cycle Profit
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-emerald-900 mt-1">
                        {formatCurrency(summary.completedCyclesGrossProfit)}
                      </p>
                      <p className="text-[9px] text-emerald-700 mt-0.5">
                        True closed cycle economics
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50/50">
                      <span className="text-[10px] font-bold uppercase text-indigo-800 block">
                        Completed Gross Margin
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-indigo-900 mt-1">
                        {summary.completedCyclesGrossMarginPct !== null
                          ? `${summary.completedCyclesGrossMarginPct.toFixed(1)}%`
                          : '—'}
                      </p>
                      <p className="text-[9px] text-indigo-700 mt-0.5">
                        {summary.completedPerformance.completedCycles.length} closed cycles
                      </p>
                    </div>
                  </div>
                </div>

                {/* 2. Current Production (4 KPIs) */}
                <div className="space-y-2 break-inside-avoid">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                    <span className="w-4 h-4 rounded-full bg-slate-800 text-white flex items-center justify-center text-[9px]">
                      1.2
                    </span>
                    <span>Current Live Production & Swine Inventory</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Active Cycles
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-slate-900 mt-1">
                        {summary.activeCyclesCount}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        Growing in pens
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Live Pigs on Feed
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-slate-900 mt-1">
                        {formatNumber(summary.livePigsOnFeed)}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        Current active head
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Pigs Sold
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-slate-900 mt-1">
                        {formatNumber(summary.totalPigsSold)}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        Commercial market head
                      </p>
                    </div>
                    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Overall Mortality
                      </span>
                      <p className="text-sm sm:text-base font-bold font-mono text-slate-900 mt-1">
                        {summary.totalMortalityHead} head
                        {summary.hasValidMortalityRate &&
                        summary.overallMortalityRatePct !== null
                          ? ` (${summary.overallMortalityRatePct.toFixed(1)}%)`
                          : ''}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        {summary.hasValidMortalityRate
                          ? 'Source-backed denominator'
                          : 'Rate omitted (no denominator)'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 3. Completed Cycle Performance Summary Table */}
                <div className="space-y-2 break-inside-avoid">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                      <span className="w-4 h-4 rounded-full bg-slate-800 text-white flex items-center justify-center text-[9px]">
                        1.3
                      </span>
                      <span>Completed Cycle Economics ({summary.completedPerformance.completedCycles.length} Cycles)</span>
                    </div>
                  </div>

                  {summary.completedPerformance.completedCycles.length > 0 ? (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-left text-[11px] border-collapse">
                        <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                          <tr>
                            <th className="py-2 px-2.5">Cycle & Raiser</th>
                            <th className="py-2 px-2.5">Batch / Dates</th>
                            <th className="py-2 px-2.5 text-right">Sold Head</th>
                            <th className="py-2 px-2.5 text-right">Revenue (₱)</th>
                            <th className="py-2 px-2.5 text-right">Costs (₱)</th>
                            <th className="py-2 px-2.5 text-right">Gross Profit (₱)</th>
                            <th className="py-2 px-2.5 text-right">Margin %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {summary.completedPerformance.completedCycles.map((c) => (
                            <tr key={c.cycleId} className="even:bg-slate-50/50">
                              <td className="py-1.5 px-2.5">
                                <span className="font-bold text-slate-900">{c.cycleCode}</span>
                                <span className="text-[10px] text-slate-500 block">{c.growerName}</span>
                              </td>
                              <td className="py-1.5 px-2.5 text-[10px] text-slate-600 font-mono">
                                <div>{c.batchCode}</div>
                                <div>{formatDate(c.startDate)} → {c.endDate ? formatDate(c.endDate) : 'Closed'}</div>
                              </td>
                              <td className="py-1.5 px-2.5 text-right font-mono font-bold text-slate-800">
                                {c.soldHead}
                              </td>
                              <td className="py-1.5 px-2.5 text-right font-mono text-slate-800">
                                {formatCurrency(c.totalRevenue)}
                              </td>
                              <td className="py-1.5 px-2.5 text-right font-mono text-slate-600">
                                {formatCurrency(c.totalProductionCost)}
                              </td>
                              <td className={`py-1.5 px-2.5 text-right font-mono font-bold ${
                                c.grossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'
                              }`}>
                                {formatCurrency(c.grossProfit)}
                              </td>
                              <td className="py-1.5 px-2.5 text-right font-mono font-semibold text-slate-800">
                                {c.grossMarginPct !== null ? `${c.grossMarginPct.toFixed(1)}%` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-500 italic p-3 bg-slate-50 rounded-lg border border-slate-200">
                      No completed cycles recorded for the selected period.
                    </p>
                  )}
                </div>

                {/* 4. Feed, Operational & Settlements (2 Columns) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 break-inside-avoid">
                  {/* Feed & Ops Activity */}
                  <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 space-y-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-800 block border-b border-slate-200 pb-1">
                      1.4 Feed & Operating Expenses
                    </span>
                    <div className="space-y-1 text-[11px]">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Feed Delivered / Consumed:</span>
                        <span className="font-mono font-bold text-slate-900">
                          {formatNumber(summary.totalFeedBags)} bags ({formatCurrency(summary.totalFeedAllocatedCost)})
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Average Feed Cost / Bag:</span>
                        <span className="font-mono text-slate-800">
                          {summary.totalFeedBags > 0
                            ? formatCurrency(summary.totalFeedAllocatedCost / summary.totalFeedBags)
                            : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Veterinary & Meds Cost:</span>
                        <span className="font-mono text-slate-800">
                          {formatCurrency(
                            summary.costMix.find((c) => c.category === 'medication')?.amount || 0
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Operating & Utility Expenses:</span>
                        <span className="font-mono text-slate-800">
                          {formatCurrency(
                            summary.costMix.find((c) => c.category === 'other')?.amount || 0
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Grower Settlements & Payouts */}
                  <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 space-y-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-800 block border-b border-slate-200 pb-1">
                      1.5 Grower Settlements & Attention
                    </span>
                    <div className="space-y-1 text-[11px]">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Settled / Frozen Liquidations:</span>
                        <span className="font-mono font-bold text-slate-900">
                          {summary.settlementPosition.finalizedCount} cycles
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Grower Net Payouts Disbursed:</span>
                        <span className="font-mono font-bold text-indigo-900">
                          {formatCurrency(summary.settlementPosition.amountOutstanding)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Ready to Settle / Unliquidated:</span>
                        <span className="font-mono text-amber-800 font-semibold">
                          {summary.managementAttention.outstandingSettlementsCount} cycles
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Open Critical Reconciliation Issues:</span>
                        <span className="font-mono text-rose-700 font-semibold">
                          {summary.managementAttention.needsReviewCount} issues
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* -------------------------------------------------------------------------
                MODE 2 & PACK: PRODUCTION PERFORMANCE ANALYSIS
                ------------------------------------------------------------------------- */}
            {(reportMode === 'current_performance' ||
              reportMode === 'management_pack') && (
              <div className={`space-y-6 print:space-y-5 ${
                reportMode === 'management_pack' ? 'print:break-before-page pt-4' : ''
              }`}>
                {reportMode === 'management_pack' && (
                  <div className="pb-1 border-b border-slate-300">
                    <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-800 text-white flex items-center justify-center text-[10px]">
                        2
                      </span>
                      <span>Production Performance Analysis</span>
                    </h2>
                  </div>
                )}

                {/* Cohort Cycles Table */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                      <span>2.1 Production Cycle Cohort Performance ({filteredCycles.length} Cycles)</span>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left text-[10px] border-collapse">
                      <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="py-2 px-2">Cycle & Raiser</th>
                          <th className="py-2 px-2">Farm Location</th>
                          <th className="py-2 px-2 text-right">Placed</th>
                          <th className="py-2 px-2 text-right">Sold</th>
                          <th className="py-2 px-2 text-right">Mort. %</th>
                          <th className="py-2 px-2 text-right">Feed Bags</th>
                          <th className="py-2 px-2 text-right">Feed Cost (₱)</th>
                          <th className="py-2 px-2 text-right">Revenue (₱)</th>
                          <th className="py-2 px-2 text-right">Gross Profit (₱)</th>
                          <th className="py-2 px-2 text-right">Margin %</th>
                          <th className="py-2 px-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {filteredCycles.map((c) => (
                          <tr key={c.cycleId} className="even:bg-slate-50/50">
                            <td className="py-1 px-2">
                              <span className="font-bold text-slate-900">{c.cycleCode}</span>
                              <span className="text-[9px] text-slate-500 block">{c.growerName}</span>
                            </td>
                            <td className="py-1 px-2 text-slate-600">{c.farmName}</td>
                            <td className="py-1 px-2 text-right font-mono">{c.placedHead ?? '—'}</td>
                            <td className="py-1 px-2 text-right font-mono font-bold text-slate-900">{c.soldHead}</td>
                            <td className="py-1 px-2 text-right font-mono">
                              {c.mortalityRatePct !== null ? `${c.mortalityRatePct.toFixed(1)}%` : '—'}
                            </td>
                            <td className="py-1 px-2 text-right font-mono">{formatNumber(c.feedBags)}</td>
                            <td className="py-1 px-2 text-right font-mono">{formatCurrency(c.feedCost)}</td>
                            <td className="py-1 px-2 text-right font-mono">{formatCurrency(c.totalRevenue)}</td>
                            <td className={`py-1 px-2 text-right font-mono font-bold ${
                              c.grossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'
                            }`}>
                              {formatCurrency(c.grossProfit)}
                            </td>
                            <td className="py-1 px-2 text-right font-mono font-semibold">
                              {c.grossMarginPct !== null ? `${c.grossMarginPct.toFixed(1)}%` : '—'}
                            </td>
                            <td className="py-1 px-2 text-center uppercase font-bold text-[8px] text-slate-600">
                              {c.status}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Raiser Performance Summary */}
                <div className="space-y-2 break-inside-avoid">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                    <span>2.2 Raiser / Grower Performance Aggregation</span>
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left text-[10px] border-collapse">
                      <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="py-2 px-2">Grower Partner</th>
                          <th className="py-2 px-2 text-right">Cycles</th>
                          <th className="py-2 px-2 text-right">Placed</th>
                          <th className="py-2 px-2 text-right">Sold Head</th>
                          <th className="py-2 px-2 text-right">Feed Cost (₱)</th>
                          <th className="py-2 px-2 text-right">Total Revenue (₱)</th>
                          <th className="py-2 px-2 text-right">Net Profit (₱)</th>
                          <th className="py-2 px-2 text-right">Avg Margin %</th>
                          <th className="py-2 px-2 text-right">Grower Payout (₱)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {raiserPerformance.map((r) => (
                          <tr key={r.growerName} className="even:bg-slate-50/50">
                            <td className="py-1 px-2 font-bold text-slate-900">{r.growerName}</td>
                            <td className="py-1 px-2 text-right font-mono">{r.totalCycles}</td>
                            <td className="py-1 px-2 text-right font-mono">{r.totalPlacedHead ?? '—'}</td>
                            <td className="py-1 px-2 text-right font-mono font-bold text-slate-900">{r.totalSoldHead}</td>
                            <td className="py-1 px-2 text-right font-mono">{formatCurrency(r.totalFeedCost)}</td>
                            <td className="py-1 px-2 text-right font-mono">{formatCurrency(r.totalRevenue)}</td>
                            <td className={`py-1 px-2 text-right font-mono font-bold ${
                              r.netGrossProfit >= 0 ? 'text-emerald-800' : 'text-rose-700'
                            }`}>
                              {formatCurrency(r.netGrossProfit)}
                            </td>
                            <td className="py-1 px-2 text-right font-mono font-semibold">
                              {r.avgGrossMarginPct !== null ? `${r.avgGrossMarginPct.toFixed(1)}%` : '—'}
                            </td>
                            <td className="py-1 px-2 text-right font-mono font-bold text-indigo-900">
                              {formatCurrency(r.totalGrowerPayout)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* -------------------------------------------------------------------------
                MODE 3 & PACK: MONTHLY FINANCIAL ACTIVITY MATRIX
                ------------------------------------------------------------------------- */}
            {(reportMode === 'current_monthly' ||
              reportMode === 'management_pack') && (
              <div className={`space-y-6 print:space-y-5 ${
                reportMode === 'management_pack' ? 'print:break-before-page pt-4' : ''
              }`}>
                {reportMode === 'management_pack' && (
                  <div className="pb-1 border-b border-slate-300">
                    <h2 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-800 text-white flex items-center justify-center text-[10px]">
                        3
                      </span>
                      <span>Monthly Financial Activity & Cashflow Matrix</span>
                    </h2>
                  </div>
                )}

                {/* 12-Month Calendar Grid */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                      <span>3.1 12-Month General Ledger Posting Matrix ({selectedYear})</span>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left text-[10px] border-collapse">
                      <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="py-2 px-2">Posting Month</th>
                          <th className="py-2 px-2 text-right">Pig Sales (₱)</th>
                          <th className="py-2 px-2 text-right">Feed Cost (₱)</th>
                          <th className="py-2 px-2 text-right">Piglets (₱)</th>
                          <th className="py-2 px-2 text-right">Meds & Direct (₱)</th>
                          <th className="py-2 px-2 text-right">Total Costs (₱)</th>
                          <th className="py-2 px-2 text-right">Net Cashflow (₱)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {monthlyRecords.map((m) => (
                          <tr key={m.monthKey} className="even:bg-slate-50/50">
                            <td className="py-1 px-2 font-bold text-slate-900">{m.monthLabel}</td>
                            <td className="py-1 px-2 text-right font-mono font-bold text-slate-900">
                              {formatCurrency(m.pigSales)}
                            </td>
                            <td className="py-1 px-2 text-right font-mono text-slate-700">
                              {formatCurrency(m.feeds)}
                            </td>
                            <td className="py-1 px-2 text-right font-mono text-slate-700">
                              {formatCurrency(m.piglets)}
                            </td>
                            <td className="py-1 px-2 text-right font-mono text-slate-700">
                              {formatCurrency(m.medsAndDirectExpenses)}
                            </td>
                            <td className="py-1 px-2 text-right font-mono font-bold text-slate-800">
                              {formatCurrency(m.totalBookedCosts)}
                            </td>
                            <td className={`py-1 px-2 text-right font-mono font-bold ${
                              m.netBookedMovement >= 0 ? 'text-emerald-800' : 'text-rose-700'
                            }`}>
                              {formatCurrency(m.netBookedMovement)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Multi-Year Comparison Table */}
                <div className="space-y-2 break-inside-avoid">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                    <span>3.2 Multi-Year Annual Financial Comparison</span>
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left text-[10px] border-collapse">
                      <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="py-2 px-2.5">Financial Year</th>
                          <th className="py-2 px-2.5 text-right">Booked Pig Sales (₱)</th>
                          <th className="py-2 px-2.5 text-right">Booked Costs (₱)</th>
                          <th className="py-2 px-2.5 text-right">Net Movement (₱)</th>
                          <th className="py-2 px-2.5 text-right">Gross Margin %</th>
                          <th className="py-2 px-2.5 text-right">Active Posting Months</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {annualSummary.map((a) => (
                          <tr key={a.year} className="even:bg-slate-50/50">
                            <td className="py-1.5 px-2.5 font-bold text-slate-900">{a.year}</td>
                            <td className="py-1.5 px-2.5 text-right font-mono font-bold text-slate-900">
                              {formatCurrency(a.pigSales)}
                            </td>
                            <td className="py-1.5 px-2.5 text-right font-mono text-slate-700">
                              {formatCurrency(a.totalBookedCosts)}
                            </td>
                            <td className={`py-1.5 px-2.5 text-right font-mono font-bold ${
                              a.netBookedMovement >= 0 ? 'text-emerald-800' : 'text-rose-700'
                            }`}>
                              {formatCurrency(a.netBookedMovement)}
                            </td>
                            <td className="py-1.5 px-2.5 text-right font-mono font-semibold">
                              {a.grossMarginPct !== null ? `${a.grossMarginPct.toFixed(1)}%` : '—'}
                            </td>
                            <td className="py-1.5 px-2.5 text-right font-mono text-slate-600">
                              {a.activeMonthsCount} months
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* =========================================================================
                FORMAL ACCOUNTING DISCLOSURES & NOTES
                ========================================================================= */}
            <div className="pt-4 border-t-2 border-slate-300 space-y-2 break-inside-avoid text-[10px] text-slate-500">
              <h3 className="font-bold text-slate-700 uppercase tracking-wider">
                Reporting Notes & Accounting Methodology Disclosures
              </h3>
              <ol className="list-decimal list-inside space-y-1 leading-relaxed">
                <li>
                  <strong>Booked Cashflow vs Cycle Economics:</strong> Booked Pig Sales and Booked Production Costs reflect transactions posted to the General Ledger within the selected calendar year. In contrast, Completed-Cycle Gross Profit reflects true lifecycle economics of cycles finished in this period, regardless of when early inputs were purchased.
                </li>
                <li>
                  <strong>Mortality Percentage Standard:</strong> Mortality rates are computed only when verified placement head count exists as a valid denominator. When denominator data is unavailable, mortality rate is rendered as <code className="font-mono text-slate-700">—</code> and never guessed or estimated.
                </li>
                <li>
                  <strong>Feed & Expense Allocation:</strong> Feeds and medications are tracked directly to individual production cycles. Transferred feed costs between pens adjust cycle balances dynamically upon transfer confirmation.
                </li>
                <li>
                  <strong>General Ledger Reconciliation:</strong> All figures reconcile to the authoritative 13-column General Ledger baseline.
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Modal Footer Controls (Hidden when printing) */}
        <DialogFooter className="p-3 bg-white border-t border-slate-200 flex flex-row items-center justify-between gap-2 shrink-0 print:hidden">
          <p className="text-[11px] text-slate-500 hidden sm:block">
            Tip: In the print dialog, select "Save as PDF" and check "Background graphics" for best visual results.
          </p>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs h-8"
            >
              Close Preview
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold gap-1.5 h-8"
            >
              <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
